import { getIn, useFormik } from "formik";
import * as Yup from "yup";
import CustomButton from "../../../../components/CustomButton";
import ContentCard from "../../../../components/misc/ContentCard";
import TextInput from "../../../../components/misc/TextInput";
import {
  checkEndpointStatus,
  endpointStatus,
  RejectedObject,
} from "../../../../helpers/apiHelper";
import { postReturnRecallDocument } from "../../../../redux/api/recall.api";
import { useAppDispatch } from "../../../../redux/hooks";
import { RecallScanDataInterface } from "../../../../redux/interfaces/recall.interface";
import Text from "../../../../styleguide/Text";
import { dateToString } from "../../../../utils/dateAndTime";

interface RecallReceiptFormValues {
  receipts: {
    idRecallLine: number;
    cantitate_cutie: string;
    cantitate_fractie: string;
  }[];
}

const quantitySchema = Yup.number()
  .transform((value, originalValue) => (originalValue === "" ? 0 : value))
  .typeError("Introdu un numar intreg")
  .integer("Introdu un numar intreg")
  .min(0, "Cantitatea nu poate fi negativa")
  .max(2147483647, "Cantitatea poate fi maximum 2147483647");

const receiptSchema = Yup.object({
  receipts: Yup.array()
    .of(
      Yup.object({
        cantitate_cutie: quantitySchema,
        cantitate_fractie: quantitySchema,
      }),
    )
    .test(
      "received-quantity",
      "Introdu cel putin o cantitate primita",
      (receipts) =>
        Boolean(
          receipts?.some(
            (receipt) =>
              Number(receipt.cantitate_cutie) > 0 ||
              Number(receipt.cantitate_fractie) > 0,
          ),
        ),
    ),
});

function RecallReceiptForm({
  document,
  disabled,
}: {
  document: RecallScanDataInterface;
  disabled: boolean;
}) {
  const dispatch = useAppDispatch();
  const lines = Array.from(
    new Map(
      document.candidateSales.map((line) => [line.idRecallLine, line]),
    ).values(),
  );
  const pharmacyName =
    document.pharmacyData.pharmacyName ?? `Farmacie ${document.clientPlId}`;
  const {
    values,
    errors,
    touched,
    status,
    isValid,
    isSubmitting,
    getFieldProps,
    submitForm,
  } = useFormik<RecallReceiptFormValues>({
    initialValues: {
      receipts: lines.map((line) => ({
        idRecallLine: line.idRecallLine,
        cantitate_cutie: "",
        cantitate_fractie: "",
      })),
    },
    validationSchema: receiptSchema,
    validateOnMount: true,
    onSubmit: async (formValues, { setStatus }) => {
      if (disabled || status || document.status !== "registered") return;
      const receipts = formValues.receipts
        .map((receipt) => ({
          idRecallLine: receipt.idRecallLine,
          cantitate_cutie: Number(receipt.cantitate_cutie),
          cantitate_fractie: Number(receipt.cantitate_fractie),
        }))
        .filter(
          (receipt) =>
            receipt.cantitate_cutie > 0 || receipt.cantitate_fractie > 0,
        );

      const result = await dispatch(
        postReturnRecallDocument({
          barcodeToken: document.barcodeToken,
          receipts,
        }),
      );
      if (
        checkEndpointStatus(result, postReturnRecallDocument) ===
        endpointStatus.rejected
      ) {
        const error = result.payload as RejectedObject | undefined;
        // Receipts are append-only; verify the document before retrying a lost response.
        if (!error?.status || error.status === 408 || error.status >= 500) {
          setStatus("requiresRescan");
        }
      }
    },
  });
  const hasReceivedQuantity = values.receipts.some(
    (receipt) =>
      Number(receipt.cantitate_cutie) > 0 ||
      Number(receipt.cantitate_fractie) > 0,
  );
  const inputsDisabled = disabled || isSubmitting || Boolean(status);

  return (
    <ContentCard cardTitle="Cantitati primite" cardHeaderClassName="mb-4">
      <div className="space-y-4">
        {lines.map((line, index) => (
          <div
            key={line.idRecallLine}
            className="grid gap-4 rounded-control border border-slate-200 p-4 md:grid-cols-[minmax(0,1fr)_minmax(180px,240px)_minmax(180px,240px)]">
            <div className="space-y-1">
              <Text isBold>{line.productCode}</Text>
              <Text>Lot: {line.lot}</Text>
              <Text>Data expirare (BBD): {dateToString(line.bbd)}</Text>
            </div>
            <TextInput
              className="!mb-0"
              name={`receipts.${index}.cantitate_cutie`}
              label="Cantitate cutie"
              type="number"
              placeholder="0"
              onlyPositiveNumbers
              inputProps={getFieldProps(`receipts.${index}.cantitate_cutie`)}
              error={getIn(errors, `receipts.${index}.cantitate_cutie`)}
              touched={getIn(touched, `receipts.${index}.cantitate_cutie`)}
              disabled={inputsDisabled}
            />
            <TextInput
              className="!mb-0"
              name={`receipts.${index}.cantitate_fractie`}
              label="Cantitate fractie"
              type="number"
              placeholder="0"
              onlyPositiveNumbers
              inputProps={getFieldProps(`receipts.${index}.cantitate_fractie`)}
              error={getIn(errors, `receipts.${index}.cantitate_fractie`)}
              touched={getIn(touched, `receipts.${index}.cantitate_fractie`)}
              disabled={inputsDisabled}
            />
          </div>
        ))}
      </div>
      {status && (
        <Text role="alert" className="mt-4 text-danger">
          Verifica din nou documentul inainte de o noua confirmare a primirii.
        </Text>
      )}
      <div className="mt-6 flex justify-end">
        <CustomButton
          variant="contained"
          disabled={
            inputsDisabled ||
            !isValid ||
            !hasReceivedQuantity ||
            document.status !== "registered"
          }
          loading={disabled || isSubmitting}
          withConfirmationModal={{
            modalTitle: "Confirma primirea documentului",
            modalSubtitle: `Confirmi primirea documentului "${document.barcodeToken}" si a cantitatilor introduse pentru farmacia "${pharmacyName}"?`,
            modalLeftButtonText: "Anuleaza",
            modalLeftButtonVariant: "outlined",
            modalRightButtonText: "Confirma primirea",
            modalRightButtonOnClick: submitForm,
          }}>
          Confirma primirea
        </CustomButton>
      </div>
    </ContentCard>
  );
}

export default RecallReceiptForm;
